import { useEffect, useRef, useState } from 'react';
import { MailCheck } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { useEmailVerification } from '@/hooks/useEmailVerification';
import { markCodeSent, wasCodeSent } from './verificationSendFlag';

const RESEND_COOLDOWN_S = 30;

/**
 * Code-entry dialog for deferred email verification (NEA-5773). Auto-sends a
 * code the first time it opens (unless one was already sent this session — see
 * `verificationSendFlag`), then lets the user enter it, resend (with a short
 * cooldown), and surfaces error/expiry messaging. On success it shows a toast,
 * runs the gated action (`onVerified`) if any, and closes.
 */
export function VerifyEmailDialog({ open, onOpenChange, onVerified }) {
	const { email, codeSent, isSending, isVerifying, error, sendCode, verifyCode, clearError } = useEmailVerification();
	const { toast } = useToast();

	const [code, setCode] = useState('');
	const [cooldown, setCooldown] = useState(0);
	const autoSentRef = useRef(false);

	// Auto-send once per open. If a code was already emailed this session
	// (proactive post-sign-up send), skip the send but treat it as sent so the
	// copy reads "we've emailed a code" and the user can still resend.
	useEffect(() => {
		if (!open) {
			autoSentRef.current = false;
			setCode('');
			clearError();
			return;
		}
		if (autoSentRef.current) return;
		autoSentRef.current = true;
		if (wasCodeSent(email)) return;
		sendCode().then((res) => {
			if (res?.success) {
				markCodeSent(email);
				setCooldown(RESEND_COOLDOWN_S);
			}
		});
	}, [open, email, sendCode, clearError]);

	// Resend cooldown tick.
	useEffect(() => {
		if (cooldown <= 0) return undefined;
		const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
		return () => clearTimeout(t);
	}, [cooldown]);

	const handleResend = async () => {
		if (cooldown > 0 || isSending) return;
		const res = await sendCode();
		if (res?.success) {
			markCodeSent(email);
			setCooldown(RESEND_COOLDOWN_S);
			toast({ title: 'Code sent', description: `We've emailed a new code to ${email}.`, variant: 'success' });
		}
	};

	const handleVerify = async (event) => {
		event?.preventDefault?.();
		const res = await verifyCode(code);
		if (res?.success) {
			toast({ title: 'Email verified', description: 'Thanks — your email address is confirmed.', variant: 'success' });
			onOpenChange(false);
			onVerified?.();
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
						<MailCheck className="h-5 w-5" />
					</div>
					<DialogTitle>Verify your email</DialogTitle>
					<DialogDescription>
						{codeSent || wasCodeSent(email) ? (
							<>
								We&apos;ve emailed a 6-digit code to <span className="font-medium text-foreground">{email}</span>. Enter it
								below to confirm your address.
							</>
						) : (
							<>
								We&apos;ll email a 6-digit code to <span className="font-medium text-foreground">{email}</span> to confirm
								your address.
							</>
						)}
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleVerify} className="space-y-4">
					<Input
						value={code}
						onChange={(e) => {
							setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
							if (error) clearError();
						}}
						inputMode="numeric"
						autoComplete="one-time-code"
						placeholder="Enter code"
						aria-label="Verification code"
						className="text-center text-lg tracking-[0.4em] placeholder:tracking-normal focus:placeholder-transparent"
						autoFocus
					/>

					{error ? <p className="text-sm text-destructive">{error}</p> : null}

					<Button type="submit" className="w-full" disabled={isVerifying || code.length < 6}>
						{isVerifying ? 'Verifying…' : 'Verify email'}
					</Button>
				</form>

				<div className="text-center text-sm text-muted-foreground">
					Didn&apos;t get it?{' '}
					<button
						type="button"
						onClick={handleResend}
						disabled={cooldown > 0 || isSending}
						className="font-medium text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-60 disabled:no-underline"
					>
						{cooldown > 0 ? `Resend in ${cooldown}s` : isSending ? 'Sending…' : 'Resend code'}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
